import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  assignBox,
  completeBoxSerials,
  getSerialPolicy,
  listLocations,
  setSerialPolicy,
  InventoryApiError,
  type InventoryBox,
  type InventoryProduct,
  type SerialMode,
  type SerialPolicy,
} from "@/lib/inventory/api";
import { useInventoryText } from "@/lib/inventory/i18n";
import { usePolicyRecoveryText } from "@/lib/inventory/policyRecoveryText";
import {
  serialLines,
  serialProblem,
  useIntakeValidationText,
} from "@/lib/inventory/intakeValidation";
import {
  ConfirmReview,
  ErrorNotice,
  Field,
  Loading,
  Panel,
  ProductPicker,
  conditionText,
  policyText,
  selectClass,
  useInventoryAccess,
  useInventoryWrite,
  useRead,
} from "./shared";

export {
  serialLines,
  serialIdentity,
  serialProblem,
} from "@/lib/inventory/intakeValidation";
type PolicyIntent = {
  policy: SerialPolicy;
  mode: Exclude<SerialMode, "UNCONFIGURED">;
  reason: string;
  acknowledged?: SerialPolicy;
  rejected?: boolean;
};
export function PolicyApproval({
  policy,
  onChanged,
  onUncertain,
  disabled = false,
}: {
  policy: SerialPolicy | null;
  onChanged: () => void | Promise<void>;
  onUncertain?: () => void;
  disabled?: boolean;
}) {
  const t = useInventoryText();
  const recovery = usePolicyRecoveryText();
  const access = useInventoryAccess();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Exclude<SerialMode, "UNCONFIGURED">>(
    !policy || policy.mode === "UNCONFIGURED" ? "REQUIRED" : policy.mode,
  );
  const [reason, setReason] = useState("");
  const [intent, setIntent] = useState<PolicyIntent | null>(null);
  const [freshPolicy, setFreshPolicy] = useState(policy);
  const [generation, setGeneration] = useState(0);
  const [readError, setReadError] = useState<unknown>(null);
  const [recovering, setRecovering] = useState(false);
  const [review, setReview] = useState(false);
  const write = useInventoryWrite();
  if (!access.supervisor) return null;
  if (!intent && policy && policy.product_id !== freshPolicy?.product_id) {
    setFreshPolicy(policy);
    setMode(policy.mode === "UNCONFIGURED" ? "REQUIRED" : policy.mode);
    setReason("");
    setReview(false);
  }
  const latest =
    policy &&
    (!freshPolicy ||
      policy.product_id !== freshPolicy.product_id ||
      policy.policy_version >= freshPolicy.policy_version)
      ? policy
      : freshPolicy;
  const base = intent?.policy || latest;
  if (!base) return null;
  const original = base;
  const busy = write.busy || recovering;
  const valid = reason.trim().length > 0 && reason.length <= 400;
  const blocked =
    !valid || write.blocked || !!readError || (!intent && disabled);
  function checkReadback(confirmed: SerialPolicy, acknowledged: SerialPolicy) {
    if (
      confirmed.product_id !== acknowledged.product_id ||
      confirmed.policy_version < acknowledged.policy_version ||
      (confirmed.policy_version === acknowledged.policy_version &&
        confirmed.mode !== acknowledged.mode)
    )
      throw new Error(recovery.readback);
  }
  async function resolved(confirmed: SerialPolicy) {
    setFreshPolicy(confirmed);
    // Invalidate every dependent view only after an authoritative outcome.
    await onChanged();
    setIntent(null);
    setGeneration((v) => v + 1);
    setReadError(null);
    write.clear();
    setReview(false);
  }
  async function confirm() {
    if (blocked || busy || !access.supervisor) return;
    const operation: PolicyIntent = intent || {
      policy: original,
      mode,
      reason,
    };
    setIntent(operation);
    // Hide stock and invalidate receiving BEFORE dispatch, not after PUT/GET.
    flushSync(() => onUncertain?.());
    const result = await write.run(
      {
        product_id: operation.policy.product_id,
        mode: operation.mode,
        reason: operation.reason,
        expected_version: operation.policy.policy_version,
        generation,
      },
      async (key) => {
        let acknowledged: SerialPolicy;
        try {
          acknowledged = await setSerialPolicy(
            operation.policy.product_id,
            operation.mode,
            operation.reason,
            operation.policy.policy_version,
            key,
          );
        } catch (error) {
          // A direct conflict rejected this operation. A GET failure did not.
          if (error instanceof InventoryApiError && error.status === 409)
            setIntent({ ...operation, rejected: true });
          throw error;
        }
        setIntent({ ...operation, acknowledged });
        const confirmed = await getSerialPolicy(operation.policy.product_id);
        checkReadback(confirmed, acknowledged);
        await resolved(confirmed);
        return confirmed;
      },
    );
    if (result) setOpen(false);
  }
  async function refresh() {
    if (busy) return;
    setReview(false);
    setRecovering(true);
    try {
      const confirmed = await getSerialPolicy(original.product_id);
      if (intent?.acknowledged) checkReadback(confirmed, intent.acknowledged);
      setFreshPolicy(confirmed);
      if (intent?.acknowledged || intent?.rejected) {
        await resolved(confirmed);
        if (intent.acknowledged) setOpen(false);
      }
      // An unchanged GET cannot prove that a lost PUT did not/will not commit.
      // Keep its original body/version and the hook's idempotency identity.
      write.clear();
      setReadError(null);
    } catch (error) {
      setReadError(error);
    } finally {
      setRecovering(false);
    }
  }
  return (
    <section className="space-y-3">
      {intent && <p aria-live="polite">{recovery.pending}</p>}
      <Button
        variant="secondary"
        onClick={() => setOpen((v) => !v)}
        disabled={busy || (!intent && disabled)}
      >
        {t.policyApproval}
      </Button>
      {open && (
        <>
          <p className="text-sm">{t.policyNote}</p>
          <Field label={t.serialPolicy}>
            <select
              className={selectClass}
              value={mode}
              disabled={busy || review || !!intent || disabled}
              onChange={(e) => setMode(e.target.value as typeof mode)}
            >
              {(["REQUIRED", "OPTIONAL", "NONE"] as const).map((m) => (
                <option key={m} value={m}>
                  {policyText(t, m)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t.policyReason}>
            <Input
              value={reason}
              maxLength={400}
              disabled={busy || review || !!intent || disabled}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <p>
            {t.product}: {original.product_id} · {t.version}:{" "}
            {original.policy_version}
          </p>
          <ErrorNotice error={readError || write.error} />
          {!review && (
            <Button disabled={blocked || busy} onClick={() => setReview(true)}>
              {t.review}
            </Button>
          )}
          {review && (
            <ConfirmReview
              title={t.policyApproval}
              busy={busy}
              blocked={blocked || !access.supervisor}
              onConfirm={() => void confirm()}
              onCancel={() => setReview(false)}
            >
              <p>
                {policyText(t, intent?.mode || mode)} · {t.version}:{" "}
                {original.policy_version}
              </p>
              <p className="whitespace-pre-wrap">{intent?.reason ?? reason}</p>
            </ConfirmReview>
          )}
          {(intent || readError || write.blocked) && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => void refresh()}
            >
              {t.retry}
            </Button>
          )}
        </>
      )}
    </section>
  );
}
export function InventoryReceive({
  box,
  product,
  onProduct,
  serialText,
  onSerialText,
  onSaved,
  onBusy,
}: {
  box: InventoryBox;
  product: InventoryProduct | null;
  onProduct: (p: InventoryProduct) => void;
  serialText: string;
  onSerialText: (s: string) => void;
  onSaved: () => Promise<void>;
  onBusy: (b: boolean) => void;
}) {
  const t = useInventoryText();
  const validationText = useIntakeValidationText();
  const access = useInventoryAccess();
  const pending = box.status !== "UNASSIGNED";
  const productId = product?.product_id ?? (pending ? box.product_id : null);
  const policy = useRead(
    () => (productId ? getSerialPolicy(productId) : Promise.resolve(null)),
    [productId],
  );
  const locations = useRead(listLocations, []);
  const [quantity, setQuantity] = useState(pending ? (box.quantity ?? 1) : 1);
  const [location, setLocation] = useState<number | "">(box.location_id ?? "");
  const [condition, setCondition] = useState(
    pending ? box.condition : "AVAILABLE",
  );
  const [review, setReview] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<unknown>(null);
  const write = useInventoryWrite();
  useEffect(() => {
    onBusy(write.busy || review || refreshing);
    return () => onBusy(false);
  }, [write.busy, review, refreshing, onBusy]);
  const serials = serialLines(serialText);
  const existing = pending ? box.serials || [] : [];
  const allSerials = [...existing, ...serials];
  const issue = serialProblem(allSerials, box.box_id, product?.ean);
  const mode = policy.data?.mode ?? "UNCONFIGURED";
  const quantityValid = Number.isSafeInteger(quantity) && quantity > 0;
  const serialIncomplete =
    mode === "REQUIRED" && allSerials.length !== quantity;
  // Completion releases only an automatic serial hold; other holds remain
  // independent of serial completeness (shopdb.inventory.complete_box_serials).
  const requestedCondition = pending
    ? box.condition === "QUARANTINE_PENDING_SERIALS"
      ? "AVAILABLE"
      : "QUARANTINED"
    : condition;
  const effectiveCondition =
    requestedCondition === "QUARANTINED" || requestedCondition === "QUARANTINE"
      ? "QUARANTINED"
      : mode === "UNCONFIGURED"
        ? "QUARANTINE_PENDING_POLICY"
        : serialIncomplete
          ? "QUARANTINE_PENDING_SERIALS"
          : "AVAILABLE";
  const valid =
    !refreshing &&
    !refreshError &&
    !!productId &&
    quantityValid &&
    !!location &&
    Number.isSafeInteger(box.row_version) &&
    !issue &&
    (mode !== "UNCONFIGURED" || requestedCondition !== "AVAILABLE") &&
    allSerials.length <= quantity &&
    (!pending || serials.length > 0) &&
    (mode !== "NONE" || allSerials.length === 0) &&
    !policy.busy &&
    !!policy.data &&
    !locations.busy &&
    !!locations.data &&
    !locations.error;
  async function confirm() {
    if (!valid || !access.write) return;
    const input = {
      product_id: productId!,
      quantity,
      location_id: Number(location),
      condition: requestedCondition,
      serials,
      expected_version: box.row_version,
    };
    const result = await write.run(
      { id: box.box_id, pending, ...input },
      async (key) => {
        if (pending)
          await completeBoxSerials(box.box_id, serials, box.row_version, key);
        else await assignBox(box.box_id, input, key);
        await onSaved();
        return true;
      },
    );
    if (result) setReview(false);
  }
  async function refresh() {
    setReview(false);
    setRefreshing(true);
    try {
      await onSaved();
      policy.refresh();
      locations.refresh();
      write.clear();
      setRefreshError(null);
    } catch (e) {
      setRefreshError(e);
    } finally {
      setRefreshing(false);
    }
  }
  if (!access.write) return <p>{t.readOnly}</p>;
  return (
    <Panel title={pending ? t.completeSerials : t.receive}>
      {!pending && (
        <ProductPicker
          disabled={review || write.busy}
          onSelect={(p) => {
            onProduct(p);
            onSerialText("");
            setReview(false);
          }}
        />
      )}
      {productId && (
        <p>
          {t.product}: {product?.full_name || product?.name || productId}
        </p>
      )}
      <ErrorNotice
        error={refreshError || policy.error || locations.error || write.error}
      />
      {(policy.busy || locations.busy) && <Loading />}
      <fieldset
        disabled={write.busy || review}
        className="grid gap-3 sm:grid-cols-3"
      >
        <Field label={t.quantity}>
          <Input
            type="number"
            min={1}
            step={1}
            disabled={pending}
            value={Number.isFinite(quantity) ? quantity : ""}
            onChange={(e) =>
              setQuantity(e.target.value === "" ? NaN : Number(e.target.value))
            }
          />
        </Field>
        <Field label={t.location}>
          <select
            className={selectClass}
            disabled={pending || locations.busy || !!locations.error}
            value={location}
            onChange={(e) =>
              setLocation(e.target.value ? Number(e.target.value) : "")
            }
          >
            <option value="">{t.select}</option>
            {locations.data?.locations.map((l) => (
              <option key={l.location_id} value={l.location_id}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.condition}>
          <select
            className={selectClass}
            disabled={pending}
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          >
            {pending ? (
              <option value={condition}>{conditionText(t, condition)}</option>
            ) : (
              <>
                <option value="AVAILABLE">{t.available}</option>
                <option value="QUARANTINED">{t.quarantined}</option>
              </>
            )}
          </select>
        </Field>
      </fieldset>
      {policy.data && !refreshing && (
        <p>
          {t.serialPolicy}: <strong>{policyText(t, mode)}</strong> ·{" "}
          {t.version}: {policy.data.policy_version}
        </p>
      )}
      {!review && (
        <PolicyApproval
          policy={policy.data}
          disabled={!policy.data || policy.busy || !!policy.error}
          onUncertain={() => {
            setRefreshing(true);
            onBusy(true);
          }}
          onChanged={async () => {
            await onSaved();
            policy.refresh();
            setRefreshing(false);
          }}
        />
      )}
      {mode === "UNCONFIGURED" &&
        productId &&
        effectiveCondition === "QUARANTINE_PENDING_POLICY" && (
          <p className="text-amber-600">{t.policyWarning}</p>
        )}
      {serialIncomplete &&
        effectiveCondition === "QUARANTINE_PENDING_SERIALS" && (
          <p className="text-amber-600">{t.serialWarning}</p>
        )}
      {existing.length > 0 && (
        <div>
          <h3>{t.recordedSerials}</h3>
          <ul>
            {existing.map((s, i) => (
              <li className="whitespace-pre-wrap font-mono" key={i}>
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Field label={t.serials}>
        <textarea
          className={`${selectClass} min-h-28 font-mono`}
          value={serialText}
          disabled={review || write.busy || mode === "NONE" || !productId}
          onChange={(e) => onSerialText(e.target.value)}
        />
      </Field>
      <p className="text-sm">{t.serialHelp}</p>
      <p aria-live="polite">
        {allSerials.length} {t.progress} {quantityValid ? quantity : "—"}
      </p>
      {issue && (
        <p role="alert">
          {issue === "invalidSerial" ? validationText.invalidSerial : t[issue]}
        </p>
      )}
      {!review && (
        <Button
          disabled={!valid || write.busy || write.blocked}
          onClick={() => setReview(true)}
        >
          {t.review}
        </Button>
      )}
      {review && (
        <ConfirmReview
          title={pending ? t.pendingReview : t.receiptReview}
          busy={write.busy}
          blocked={!valid || write.blocked || !access.write}
          onConfirm={() => void confirm()}
          onCancel={() => setReview(false)}
        >
          <dl>
            <dt>{t.product}</dt>
            <dd>{product?.full_name || product?.name || productId}</dd>
            <dt>{t.quantity}</dt>
            <dd>{quantity}</dd>
            <dt>{t.location}</dt>
            <dd>
              {
                locations.data?.locations.find(
                  (l) => l.location_id === location,
                )?.name
              }
            </dd>
            <dt>{t.condition}</dt>
            <dd>{conditionText(t, effectiveCondition)}</dd>
            <dt>{t.version}</dt>
            <dd>{box.row_version}</dd>
            <dt>{t.serialPolicy}</dt>
            <dd>{policyText(t, mode)}</dd>
          </dl>
          <ul>
            {serials.map((s, i) => (
              <li className="whitespace-pre-wrap font-mono" key={i}>
                {s}
              </li>
            ))}
          </ul>
          {mode === "UNCONFIGURED" &&
            effectiveCondition === "QUARANTINE_PENDING_POLICY" && (
              <p>{t.policyWarning}</p>
            )}
          {serialIncomplete &&
            effectiveCondition === "QUARANTINE_PENDING_SERIALS" && (
              <p>{t.serialWarning}</p>
            )}
        </ConfirmReview>
      )}
      <Button
        variant="secondary"
        disabled={write.busy || refreshing}
        onClick={() => void refresh()}
      >
        {t.retry}
      </Button>
    </Panel>
  );
}
